import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { logError } from '@zg-rentals/log-error';
import { shimmerStyles } from '@zg-rentals/loading-components';

import { Alert, Box, Button, Combobox, Form, FormField, FormHelp, Label, Text } from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';

import {
  useRentalManagerRenterContactWebApiHandler_getLikelyTenants,
  useRentalManagerRenterContactWebApiHandler_getTenants,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import {
  type RentalManagerRenterContactLikelyTenantDto,
  RentalManagerRenterWebApiHandler_addOrUpdate,
  RentalManagerUserInfoWebApiHandler_get,
  RenterRoleType,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import { DatarouterError } from '@zg-rentals/web-base';

import TenantForm from '../TenantForm';
import FormFieldLeaseTerm, { LEASE_TERM_OPTIONS } from '../FormFieldLeaseTerm';

import type { Client } from '@zg-rentals/http-client';
import type { TenantFormProps } from '../TenantForm';
import type {
  RentalManagerRenterAddOrUpdateRenterDto,
  RentalManagerRenterAddOrUpdateRentersRequestDto,
  RentalManagerRenterAddOrUpdateRentersResponseDto,
  RenterInfoRenterAddedDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

export const SECTION_ORDER = {
  LEASE_FIRST: 'lease-first',
  TENANTS_FIRST: 'tenants-first',
} as const;

export type SectionOrder = (typeof SECTION_ORDER)[keyof typeof SECTION_ORDER];

export type TenantSelectFormBodyProps = {
  client: Client;
  hideLeaseTermUntilTenantSelected?: boolean;
  isOpen?: boolean;
  leaseTermsSubtitle?: string;
  leaseTermsTitle?: string;
  listingAlias: string;
  onSubmitError?: (error: string) => void;
  onSubmitSuccess?: (addedRenters?: Array<RenterInfoRenterAddedDto>) => void;
  onValidationChange?: (isValid: boolean) => void;
  prePopulateExistingTenants?: boolean;
  sectionOrder?: SectionOrder;
  includeActiveTenants?: boolean;
  tenantSelectionSubtitle?: string;
  tenantSelectionTitle?: string;
};

export type TenantSelectFormBodyRef = {
  addRenters: () => Promise<void>;
};

type ComboboxData = {
  value: Array<string>;
  meta: Record<string, unknown>;
  calls: number;
};

type DropdownSelectLeaseTermData = {
  value: string;
  meta: Record<string, unknown>;
  calls: number;
};

export const FORM_LEASE_TERMS_CONTENT = {
  FORM_DATES_TITLE: 'What are your lease terms?',
} as const;

export const FORM_SELECT_TENANT_CONTENT = {
  FORM_ADD_TENANTS_TITLE: 'Who are your tenants?',
  FORM_ADD_TENANTS_SUBTITLE: '',
  LABEL: 'Tenant name',
  FORM_HELP: 'Enter a name to select your tenants. If they are not listed, please add them as a new tenant.',
} as const;

// required for combobox display
const EXTENDED_TENANT_KEYS = { VALUE: 'value', LABEL: 'label' } as const;

const OPTIONS = { LIMIT_TAGS_SHOWN: 5, PLACEHOLDER: 'Select tenants', DEFAULT_RENTER_ID_AS_STRING: '' } as const;

type TenantComboboxOption = RentalManagerRenterContactLikelyTenantDto & {
  value: string;
  label: string;
};

const emptyTenantFormList: RentalManagerRenterAddOrUpdateRenterDto = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  rawEmailAddress: '',
  renterId: OPTIONS.DEFAULT_RENTER_ID_AS_STRING,
  role: {
    type: RenterRoleType.TENANT,
    roleStartDateMs: 0,
  },
};

const getMsFromDate = (date: string) => {
  return date ? new Date(date).getTime() : null;
};

const defaultStartDate = '';
const defaultEndDate = '';

/**
 * TenantSelectFormBody Component
 *
 * Renders the form body for tenant selection and lease term configuration.
 * This component is designed to be consumed by modal wrappers or other containers
 * to create flexible tenant management flows.
 *
 * @param {TenantSelectFormBodyProps} props
 * @param {Client} props.client - The HTTP client for API calls
 * @param {boolean} props.hideLeaseTermUntilTenantSelected - Hides lease fields until at least one tenant is selected
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {string} props.leaseTermsSubtitle - Subheading below the lease terms title
 * @param {string} props.leaseTermsTitle - Title for the lease terms section
 * @param {string} props.listingAlias - The listing alias for the property
 * @param {Function} props.onSubmitError - Callback when submission fails
 * @param {Function} props.onSubmitSuccess - Callback when tenants are successfully added
 * @param {Function} props.onValidationChange - Callback when form validation state changes
 * @param {boolean} props.prePopulateExistingTenants - Pre-populates combobox with existing tenants on load
 * @param {'tenants-first' | 'lease-first'} props.sectionOrder - Determines display order of sections
 * @param {boolean} props.includeActiveTenants - Includes active tenants in combobox when true
 * @param {string} props.tenantSelectionSubtitle - Subheading below the tenant section title
 * @param {string} props.tenantSelectionTitle - Heading for the tenant section (e.g., "Who are your tenants?")
 *
 * @returns {JSX.Element}
 */
const TenantSelectFormBody = forwardRef<TenantSelectFormBodyRef, TenantSelectFormBodyProps>(
  (
    {
      client,
      hideLeaseTermUntilTenantSelected = false,
      isOpen,
      leaseTermsSubtitle,
      leaseTermsTitle = FORM_LEASE_TERMS_CONTENT.FORM_DATES_TITLE,
      listingAlias,
      onSubmitError,
      onSubmitSuccess,
      onValidationChange,
      prePopulateExistingTenants = false,
      sectionOrder = SECTION_ORDER.LEASE_FIRST,
      includeActiveTenants = false,
      tenantSelectionSubtitle = FORM_SELECT_TENANT_CONTENT.FORM_ADD_TENANTS_SUBTITLE,
      tenantSelectionTitle = FORM_SELECT_TENANT_CONTENT.FORM_ADD_TENANTS_TITLE,
    },
    ref,
  ) => {
    const [comboboxData, setComboboxData] = useState<ComboboxData>({ value: [], meta: {}, calls: 0 });
    const [comboboxLeaseTermData, setComboboxLeaseTermData] = useState<DropdownSelectLeaseTermData>({
      value: LEASE_TERM_OPTIONS[0].value,
      meta: {},
      calls: 0,
    });

    const [startDate, setStartDate] = useState<string>(defaultStartDate);
    const [endDate, setEndDate] = useState<string>(defaultEndDate);

    const [serverError, setServerError] = useState<string | null>(null);

    const [formError, setFormError] = useState<Record<string, string>>({
      startDate: '',
      endDate: '',
    });

    const [tenantFormList, setTenantFormList] = useState<Record<string, TenantFormProps>>({});

    const getLikelyTenantsPostData = { listingAlias };
    const {
      data: getLikelyTenantsData,
      isFetching,
      isLoading,
      error,
    } = useRentalManagerRenterContactWebApiHandler_getLikelyTenants(getLikelyTenantsPostData, {
      httpClient: client,
    });

    const {
      data: getTenantsData,
      isFetching: isFetchingTenants,
      isLoading: isLoadingTenants,
      isError: _isErrorTenants,
      error: _tenantsError,
    } = useRentalManagerRenterContactWebApiHandler_getTenants(
      {
        listingAlias,
      },
      {
        httpClient: client,
        enabled: prePopulateExistingTenants,
        refetchOnMount: 'always',
      },
    );

    const previousComboboxValueRef = useRef<Array<string>>([]);
    const previousEndDateRef = useRef<string>(defaultEndDate);
    const hasInitializedTenantsRef = useRef(false);

    // filter out existing tenants
    const filteredLikelyTenants = useMemo(() => {
      if (includeActiveTenants) {
        // return all tenants
        return getLikelyTenantsData?.likelyTenants;
      }

      // filter out inactive tenants
      return getLikelyTenantsData?.likelyTenants?.filter((tenant) => tenant.isTenant === false);
    }, [getLikelyTenantsData?.likelyTenants, includeActiveTenants]);

    /**
     * The list of Tenants that have been returned by the getLikelyTenants API
     *
     * These need to be formatted for combobox display
     */
    const formattedLikelyTenants = useMemo<Array<TenantComboboxOption> | undefined>(() => {
      // keep track of duplicate labels (fullName)
      const fullNameMap: Array<string> = [];
      let hasDuplicateFullName = false;
      let count = 1;

      return filteredLikelyTenants?.map((tenant) => {
        hasDuplicateFullName = false;
        const fullName = tenant?.fullName ?? '';

        // we need a unique label in order for remove to work
        if (fullNameMap.includes(fullName)) {
          hasDuplicateFullName = true;
          count++;
        } else {
          fullNameMap.push(fullName);
        }

        return {
          ...tenant,
          [EXTENDED_TENANT_KEYS.VALUE]: tenant.renterId,
          [EXTENDED_TENANT_KEYS.LABEL]: hasDuplicateFullName ? `${fullName} (${count})` : fullName,
        };
      });
    }, [filteredLikelyTenants]);

    /**
     * Find a tenant by value from the likely tenants list
     */
    const findTenantByValue = useCallback(
      (value: string) => {
        return formattedLikelyTenants?.find((tenant) => tenant[EXTENDED_TENANT_KEYS.VALUE] === value);
      },
      [formattedLikelyTenants],
    );

    /**
     * Tenants that have been selected from the comboxbox
     */
    const selectedTenantList = useMemo(
      () =>
        comboboxData.value.map((value) => {
          const tenant = { ...findTenantByValue(value) };

          // remove combobox keys
          delete tenant?.[EXTENDED_TENANT_KEYS.VALUE];
          delete tenant?.[EXTENDED_TENANT_KEYS.LABEL];

          return tenant;
        }),
      [comboboxData.value, findTenantByValue],
    );

    // conditional settings
    const placeholder = selectedTenantList.length === 0 ? OPTIONS.PLACEHOLDER : '';

    // has at least one tenant in the tenant form list
    const hasTenantsSelected = Object.keys(tenantFormList).length > 0;

    const getTenantFormValidation = (tenantFormList: Record<string, TenantFormProps>) => {
      const hasFieldError = Object.values(tenantFormList).some((tenantForm) => tenantForm.hasFieldError);

      return hasFieldError;
    };

    const isFixedTermLeaseTerm = comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[0].value;
    const isEndDateRequired = isFixedTermLeaseTerm;

    const isFormValid =
      !!startDate &&
      (!isEndDateRequired || !!endDate) &&
      Object.keys(tenantFormList).length > 0 &&
      !getTenantFormValidation(tenantFormList);

    const shouldShowLeaseTerms = hideLeaseTermUntilTenantSelected ? hasTenantsSelected : true;

    /**
     * Notify parent of validation state changes
     */
    useEffect(() => {
      onValidationChange?.(isFormValid);
    }, [isFormValid, onValidationChange]);

    /**
     * Sync the combobox data to the tenant form list
     */
    useEffect(
      () => {
        const { value: nextValue } = comboboxData;

        const previousValue = previousComboboxValueRef.current;
        const isAddingTenant = nextValue.length > previousValue.length;
        const difference: Array<string> = isAddingTenant
          ? nextValue.filter((value) => !new Set(previousValue).has(value))
          : previousValue.filter((value) => !new Set(nextValue).has(value));

        // update the previous value reference
        previousComboboxValueRef.current = nextValue;

        // handle actions
        if (difference.length === 0) {
          return;
        }

        const foundTenant = findTenantByValue(difference[0]);

        // no tenant found
        if (!foundTenant) {
          return;
        }

        if (isAddingTenant) {
          const renter = {
            rawEmailAddress: foundTenant?.emailAddress,
            firstName: foundTenant?.firstName,
            lastName: foundTenant?.lastName,
            phoneNumber: foundTenant?.phoneNumber,
            renterId: foundTenant?.renterId,
          } as RentalManagerRenterAddOrUpdateRenterDto;

          addTenantToTenantFormList(renter);
        } else {
          removeTenantFromTenantFormList(foundTenant.renterId);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [comboboxData.value, findTenantByValue],
    );

    useEffect(() => {
      // keep a reference to the previous end date
      if (comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[0].value) {
        setEndDate(previousEndDateRef.current);
      }

      // month-to-month lease term
      if (comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[1].value) {
        setEndDate(defaultEndDate);
      }
    }, [comboboxLeaseTermData.value]);

    /**
     * Validate form fields
     */
    useEffect(() => {
      if (endDate && endDate !== defaultEndDate) {
        previousEndDateRef.current = endDate;
      }
    }, [startDate, endDate]);

    /**
     * Pre-populate combobox with existing tenants on component load
     * Matches tenants from getTenantsData with likelyTenants by renterId
     */
    useEffect(() => {
      if (!prePopulateExistingTenants) {
        return;
      }

      // Only run once on initial load when tenants data is ready
      if (
        hasInitializedTenantsRef.current ||
        isLoadingTenants ||
        !getTenantsData?.tenants?.length ||
        !getLikelyTenantsData?.likelyTenants?.length
      ) {
        return;
      }

      hasInitializedTenantsRef.current = true;

      // Create a map of likely tenants by renterId for faster lookup
      const likelyTenantsMap = new Map(getLikelyTenantsData.likelyTenants.map((tenant) => [tenant.renterId, tenant]));

      // Find matching tenants from getTenantsData that exist in likelyTenants
      const matchingTenants = getTenantsData.tenants.filter(
        (tenant) => tenant.renterId && likelyTenantsMap.has(tenant.renterId),
      );

      if (matchingTenants.length > 0) {
        const matchingRenterIds = matchingTenants.map((tenant) => tenant.renterId as string);

        // Build tenantFormList with all matching tenants
        const newTenantFormList: Record<string, TenantFormProps> = {};

        matchingTenants.forEach((tenant, index) => {
          const id = index + 1;

          newTenantFormList[id] = {
            id,
            title: `Tenant #${id}`,
            renter: {
              rawEmailAddress: tenant.emailAddress,
              firstName: tenant.firstName,
              lastName: tenant.lastName,
              phoneNumber: tenant.phoneNumber,
              renterId: tenant.renterId,
            } as RentalManagerRenterAddOrUpdateRenterDto,
          };
        });

        // Update previousComboboxValueRef to prevent sync useEffect from re-processing
        previousComboboxValueRef.current = matchingRenterIds;

        setTenantFormList(newTenantFormList);
        setComboboxData((prev) => ({
          ...prev,
          value: matchingRenterIds,
          calls: prev.calls + 1,
        }));
      }
    }, [prePopulateExistingTenants, isLoadingTenants, getTenantsData?.tenants, getLikelyTenantsData?.likelyTenants]);

    const clearServerError = useCallback(() => {
      setServerError(null);
    }, []);

    const fetchUser = async () => {
      const response = await RentalManagerUserInfoWebApiHandler_get({}, client).catch((error) => {
        throw error;
      });

      return response;
    };

    /**
     * Clear the selected tenants
     * Does not remove manually added tenants
     */
    const clearSelectedTenantsFromFormList = () => {
      const newTenantFormList = Object.fromEntries(
        Object.entries(tenantFormList).filter(
          ([, value]) =>
            (value.renter as RentalManagerRenterAddOrUpdateRenterDto).renterId === OPTIONS.DEFAULT_RENTER_ID_AS_STRING,
        ),
      );

      setTenantFormList(newTenantFormList);
    };

    const addTenantToTenantFormList = (renter: RentalManagerRenterAddOrUpdateRenterDto | null = null) => {
      const nextId = Object.keys(tenantFormList).length ? Math.max(...Object.keys(tenantFormList).map(Number)) + 1 : 1;

      const tenantFormProps: Partial<TenantFormProps> = {
        id: nextId,
        title: `Tenant #${nextId}`,
        renter: renter || Object.assign({}, emptyTenantFormList),
      };

      setTenantFormList({
        ...tenantFormList,
        [nextId]: tenantFormProps,
      });
    };

    const removeTenantFromTenantFormList = (renterId: string) => {
      const copyTenantFormList = { ...tenantFormList };

      Object.entries(copyTenantFormList).forEach(([key, value]) => {
        if (value.renter?.renterId === renterId) {
          delete copyTenantFormList[key];
        }
      });

      setTenantFormList(copyTenantFormList);
    };

    const handleComboboxClear = () => {
      setComboboxData({ value: [], meta: {}, calls: 0 });
      clearSelectedTenantsFromFormList();
    };

    const handleComboboxChange = useCallback(
      (value: Array<string>) => {
        return setComboboxData({ value, meta: {}, calls: comboboxData.calls + 1 });
      },
      [comboboxData.calls],
    );

    const handleComboboxLeaseTermChange = useCallback(
      (value: string, meta: Record<string, unknown>) => {
        return setComboboxLeaseTermData({ value, meta, calls: comboboxLeaseTermData.calls + 1 });
      },
      [comboboxLeaseTermData.calls],
    );

    /**
     * Delete a tenant from the tenant form list
     *
     * @param {number} id - The id of the tenant to delete
     */
    const handleDeleteTenantClick = (id: number) => {
      const copyTenantFormList = { ...tenantFormList };
      const copyComboboxDataValue = [...comboboxData.value];
      const targetRenter = copyTenantFormList[id].renter;
      const { renterId } = targetRenter as RentalManagerRenterAddOrUpdateRenterDto;
      const newComboboxDataValue = copyComboboxDataValue.filter((value) => value !== renterId);

      delete copyTenantFormList[id];

      setTenantFormList(copyTenantFormList);

      // sync combobox data
      setComboboxData({ value: newComboboxDataValue, meta: {}, calls: comboboxData.calls + 1 });
    };

    const handleChange = ({
      id,
      value,
      name,
    }: {
      id: number;
      value: string;
      name: string;
      hasFieldError?: boolean;
    }) => {
      const copyTenantFormList = { ...tenantFormList };

      if (copyTenantFormList[id]) {
        // @ts-expect-error - Dynamic property assignment on renter object
        copyTenantFormList[id]['renter'][name as keyof RentalManagerRenterAddOrUpdateRenterDto] = value;
      }

      setTenantFormList(copyTenantFormList);
    };

    const updateTenantFormListFieldError = ({ id, hasFieldError }: { id: number; hasFieldError: boolean }) => {
      const copyTenantFormList = { ...tenantFormList };

      if (copyTenantFormList[id]) {
        copyTenantFormList[id]['hasFieldError'] = hasFieldError;
      }

      setTenantFormList(copyTenantFormList);
    };

    const addRentersApiCall = async (postData: RentalManagerRenterAddOrUpdateRentersRequestDto) => {
      const response = await RentalManagerRenterWebApiHandler_addOrUpdate(postData, client).catch((error) => {
        throw error;
      });

      return response as RentalManagerRenterAddOrUpdateRentersResponseDto;
    };

    const addRenters = useCallback(async (): Promise<void> => {
      // Guard: prevent submission if form is invalid or no tenants
      if (!isFormValid || Object.keys(tenantFormList).length === 0) {
        const errorMsg = 'Please add at least one tenant and complete all required fields';
        setServerError(errorMsg);
        onSubmitError?.(errorMsg);
        throw new Error(errorMsg);
      }

      const response = await fetchUser();

      const { userToken = null } = response ?? {};

      const formattedData: RentalManagerRenterAddOrUpdateRentersRequestDto = {
        listingAlias,
        referenceId: userToken ? `zillow_${userToken}` : 'zillow_0000000000',
        referenceIdType: 'manualEntryId',
        renters: Object.values(tenantFormList).map(({ renter }) => {
          // remove renterId if it is empty
          if (renter.renterId === OPTIONS.DEFAULT_RENTER_ID_AS_STRING) {
            delete renter.renterId;
          }

          // append role data to all items
          renter.role = {
            type: RenterRoleType.TENANT,
            roleStartDateMs: getMsFromDate(startDate) ?? 0,
            roleEndDateMs: !endDate ? undefined : (getMsFromDate(endDate) ?? 0),
          };

          // remove undefined roleEndDateMs
          if (renter.role.roleEndDateMs === undefined) {
            delete renter.role.roleEndDateMs;
          }

          return renter;
        }),
      };

      clearServerError();

      try {
        const result = await addRentersApiCall(formattedData);
        onSubmitSuccess?.(result?.rentersAdded);
      } catch (error) {
        const displayError = DatarouterError.getDisplayMessage(error as DatarouterError);
        let errorMessage = displayError;

        // displayError might only be a string that can not be converted via JSON.parse
        try {
          const parsed = JSON.parse(displayError);

          if (parsed?.error?.message) {
            errorMessage = parsed.error.message;
          }
        } catch {
          // displayError is not JSON, use as is
        }

        const capitalizedError =
          typeof errorMessage === 'string' && errorMessage.length > 0
            ? errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1)
            : 'An unexpected error occurred';

        setServerError(capitalizedError);
        onSubmitError?.(capitalizedError);

        logError({
          error: new Error(`Error adding tenants: ${capitalizedError}`),
          errorType: 'AddOrUpdateRentersError',
          context: 'TenantSelectFormBody@addRenters',
        });

        throw error;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFormValid, tenantFormList, startDate, endDate, listingAlias, onSubmitSuccess, onSubmitError]);

    const handleAddTenantButtonClick = () => {
      addTenantToTenantFormList(null);
    };

    /**
     * Reset form to initial state
     */
    const resetForm = useCallback(() => {
      setTenantFormList({});
      setFormError({ startDate: '', endDate: '' });
      setStartDate(defaultStartDate);
      setEndDate(defaultEndDate);
      setComboboxData({ value: [], meta: {}, calls: 0 });
      setComboboxLeaseTermData({ value: LEASE_TERM_OPTIONS[0].value, meta: {}, calls: 0 });
      setServerError(null);
      previousComboboxValueRef.current = [];
      previousEndDateRef.current = defaultEndDate;
      hasInitializedTenantsRef.current = false;
    }, []);

    /**
     * Clear server error when modal opens and reset form when modal closes
     */
    useEffect(() => {
      if (isOpen) {
        clearServerError();
      }

      return () => {
        if (isOpen === false) {
          resetForm();
        }
      };
    }, [isOpen, resetForm, clearServerError]);

    /**
     * Expose addRenters method to parent component
     */
    useImperativeHandle(ref, () => ({
      addRenters,
    }));

    const renderLoadingSkeleton = () => {
      return (
        <Box data-testid="TenantSelectFormBodySkeleton" css={{ display: 'flex', flexDirection: 'column', gap: '600' }}>
          {/* Title skeleton */}
          <Box data-loading={true} css={{ ...shimmerStyles, borderRadius: '100', width: '50%', height: '600' }} />

          {/* Subtitle skeleton (optional) */}
          <Box data-loading={true} css={{ ...shimmerStyles, borderRadius: '100', width: '70%', height: '400' }} />

          {/* Form field skeleton */}
          <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
            {/* Label skeleton */}
            <Box data-loading={true} css={{ ...shimmerStyles, borderRadius: '100', width: '25%', height: '400' }} />
            {/* Input skeleton */}
            <Box data-loading={true} css={{ ...shimmerStyles, borderRadius: '100', width: '100%', height: '1100' }} />
            {/* Help text skeleton */}
            <Box data-loading={true} css={{ ...shimmerStyles, borderRadius: '100', width: '80%', height: '350' }} />
          </Box>

          {/* Create new tenant button skeleton */}
          <Box data-loading={true} css={{ ...shimmerStyles, borderRadius: '100', width: '160px', height: '500' }} />
        </Box>
      );
    };

    const renderLeaseTermsContainer = () => {
      return (
        <Form>
          <Box data-testid="LeaseTermsContainer">
            <Box>
              <Text textStyle="body-lg-bold">{leaseTermsTitle}</Text>
            </Box>

            {leaseTermsSubtitle && (
              <Box
                css={{
                  marginTop: 'tighter',
                }}
              >
                <Text textStyle="body">{leaseTermsSubtitle}</Text>
              </Box>
            )}

            <Box
              css={{
                marginBottom: 'loose',
              }}
            />

            <FormFieldLeaseTerm
              startDate={startDate}
              endDate={endDate}
              comboboxLeaseTermData={comboboxLeaseTermData}
              formError={formError}
              isEndDateRequired={isEndDateRequired}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onLeaseTermChange={handleComboboxLeaseTermChange}
              onFormErrorChange={setFormError}
            />
          </Box>

          <Box
            css={{
              marginBottom: 'loose',
            }}
          />
        </Form>
      );
    };

    const renderTenantFormContainer = () => {
      return (
        <>
          <Box data-testid="TenantFormContainer">
            <Box data-testid="FormAddTenantsContainer">
              <Box
                css={{
                  marginBottom: 'tight',
                }}
              >
                <Text textStyle="body-lg-bold">{tenantSelectionTitle}</Text>
              </Box>

              {tenantSelectionSubtitle && (
                <Box
                  data-testid="TenantSelectionSubtitle"
                  css={{
                    marginTop: 'tighter',
                  }}
                >
                  <Text textStyle="body">{tenantSelectionSubtitle}</Text>
                </Box>
              )}

              <Box
                css={{
                  marginBottom: 'loose',
                }}
              />

              <FormField
                className="fs-mask"
                data-testid="TenantName"
                label={<Label required={false}>{FORM_SELECT_TENANT_CONTENT.LABEL}</Label>}
                control={
                  <Combobox
                    value={comboboxData.value}
                    showLabelForValue={true}
                    options={formattedLikelyTenants || []}
                    fluidDropdown={true}
                    limitChipsShown={OPTIONS.LIMIT_TAGS_SHOWN}
                    loading={isFetching || isLoading || isFetchingTenants || isLoadingTenants}
                    placeholder={placeholder}
                    onChange={handleComboboxChange}
                    onClear={handleComboboxClear}
                  />
                }
                description={<FormHelp>{FORM_SELECT_TENANT_CONTENT.FORM_HELP}</FormHelp>}
              />
            </Box>

            {Object.entries(tenantFormList).length > 0 && (
              <Box
                css={{
                  marginBottom: 'tight',
                }}
              />
            )}

            {Object.entries(tenantFormList).map(([key, tenantFormProps], index) => {
              return (
                <Box key={key} css={{ marginBottom: 'loose' }}>
                  <TenantForm
                    {...tenantFormProps}
                    title={`Tenant #${index + 1}`}
                    onDeleteTenantClick={handleDeleteTenantClick}
                    onChange={handleChange}
                    onFieldError={updateTenantFormListFieldError}
                  />
                </Box>
              );
            })}
          </Box>

          <Box
            css={{
              marginBottom: 'looser',
            }}
          />

          <Button
            tone="brand"
            emphasis="outlined"
            icon={<IconPlusCircleFilled />}
            iconPosition="end"
            onClick={handleAddTenantButtonClick}
          >
            Create new tenant
          </Button>

          <Box
            css={{
              marginBottom: sectionOrder === SECTION_ORDER.TENANTS_FIRST ? 'loose' : 0,
            }}
          />
        </>
      );
    };

    if (error) {
      return (
        <Box css={{ marginBottom: '200' }}>
          <Alert tone="warning">Failed to load tenant data</Alert>
        </Box>
      );
    }

    if (isLoadingTenants || isLoading) {
      return (
        <Box className="fs-unmask" data-testid="TenantSelectFormBodySkeletonContainer">
          {renderLoadingSkeleton()}
        </Box>
      );
    }

    return (
      <Box className="fs-unmask" data-testid="TenantSelectFormBody">
        {serverError && (
          <>
            <Box
              data-testid="ErrorContainer"
              css={{
                marginBottom: 'loose',
              }}
            >
              <Alert tone="warning">{serverError}</Alert>
            </Box>
          </>
        )}

        {sectionOrder === SECTION_ORDER.LEASE_FIRST ? (
          <>
            {shouldShowLeaseTerms && renderLeaseTermsContainer()}
            {renderTenantFormContainer()}
          </>
        ) : (
          <>
            {renderTenantFormContainer()}
            {shouldShowLeaseTerms && renderLeaseTermsContainer()}
          </>
        )}
      </Box>
    );
  },
);

TenantSelectFormBody.displayName = 'TenantSelectFormBody';

export default memo(TenantSelectFormBody);
